import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "419",
  "slug": "end-of-year-geography-activities-classroom",
  "title": "End-of-Year Geography Activities for the Classroom",
  "excerpt": "When the units are done and attention is short, these low-prep, high-energy geography activities keep the last weeks of school engaging and still worth showing up for.",
  "date": "2026-06-12",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1524661135-423995f22d0b?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxjbGFzc3Jvb20lMjB3b3JsZCUyMG1hcHxlbnwxfDB8fHwxNzgxMjUyMzA1fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "3 min",
  "author": "EarthGuessr Team",
  "tldr": "The best end-of-year geography activities are low-prep and high-energy: class guessing-game tournaments, dream-trip projects, a few outdoor map and compass tasks, and a reflection on what students learned about the world this year.",
  "content": [
    {
      "type": "paragraph",
      "text": "The final weeks of the school year are their own kind of challenge. The curriculum is wrapped up, grades are mostly in, the weather is pulling everyone toward the window, and attention spans are short. This is exactly when geography shines, because the subject is naturally visual, playful, and easy to turn into a game. Here are activities that keep the last stretch of term genuinely worthwhile without demanding hours of preparation."
    },
    {
      "type": "heading",
      "text": "Why end-of-year geography works"
    },
    {
      "type": "paragraph",
      "text": "Choose a short geography task with a visible result, such as a labelled map, a comparison, or a question for another team. Looking at a strange landscape and asking where on Earth it could be is engaging even when no one is in the mood for a worksheet. The trick is to lean into that, swapping formal assessment for curiosity, teamwork, and a bit of friendly competition."
    },
    {
      "type": "heading",
      "text": "Run a class guessing-game tournament"
    },
    {
      "type": "paragraph",
      "text": "Nothing fills a low-energy afternoon like a friendly competition. Project a satellite guessing game on the board and play as a class or in teams, with everyone debating the clues out loud before the reveal. A bracket format, where teams advance round by round, adds just enough stakes to keep the room invested. It rewards exactly the observation skills students built all year, and it needs almost no setup."
    },
    {
      "type": "heading",
      "text": "Plan a dream trip"
    },
    {
      "type": "paragraph",
      "text": "Give students a budget, a number of days, and a blank map, and let them plan a journey anywhere in the world. They map a route, justify each stop, and account for distances, climate, and what they would actually see. It is open-ended enough to absorb different ability levels, and it quietly pulls together map skills, climate, and culture into one creative project."
    },
    {
      "type": "heading",
      "text": "Get outside and get hands-on"
    },
    {
      "type": "paragraph",
      "text": "Good weather is an asset, not a distraction, and building things suits the end-of-year mood. A few classics work every time:"
    },
    {
      "type": "list",
      "items": [
        "A simple orienteering loop or compass-and-cardinal-directions exercise in the schoolyard",
        "Salt-dough or paper relief maps that show mountains and valleys in three dimensions",
        "A class flag gallery, with each student researching one country's flag and its meaning",
        "A giant floor map drawn in chalk outside, used for a walk-the-continents quiz",
        "Travel posters or brochures for a country or region of each student's choice"
      ]
    },
    {
      "type": "heading",
      "text": "Quick five-minute fillers"
    },
    {
      "type": "paragraph",
      "text": "For the odd spare moments between events, keep a few fast rounds ready: a where-in-the-world image flashed on the board, a capital-city quick-fire, a guess-the-country-from-its-outline challenge, or a single satellite view for the class to puzzle over together. These need no preparation and turn dead time into something students actually enjoy."
    },
    {
      "type": "heading",
      "text": "End on reflection"
    },
    {
      "type": "paragraph",
      "text": "Close the year by asking students what surprised them most about the world this year, which place they would most like to visit, or one thing they now notice that they never did before. A short written or spoken reflection turns a year of facts into something personal, and it is a calm, satisfying note to finish on."
    },
    {
      "type": "paragraph",
      "text": "However you fill the final weeks, the goal is the same: keep students looking at the world with curiosity right up to the last day. A quick round of EarthGuessr on the board is an easy way to do exactly that, and to send everyone into summer still wondering where on Earth they have just landed."
    }
  ]
};
